import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ExternalLink, MonitorPlay, Search, Users } from "lucide-react";

import { fetchSallesRequest } from "../../../redux/actions/sallesActions";
import "./SallesVirtuellesPage.css";

const normalize = (value) => String(value || "").trim();
const normalizeUpper = (value) => normalize(value).toUpperCase();

const slugify = (value) =>
    normalize(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s-]/g, " ")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .toUpperCase();

const extractFirstUrl = (...values) => {
    for (const value of values) {
        const match = String(value || "").match(/https?:\/\/[^\s,;]+/i);
        if (match) return match[0];
    }
    return "";
};

const inferProviderFromUrl = (url) => {
    const lower = String(url || "").toLowerCase();
    if (!lower) return "";
    if (lower.includes("meet.jit.si")) return "Jitsi";
    if (lower.includes("teams.microsoft.com")) return "Microsoft Teams";
    if (lower.includes("zoom.us")) return "Zoom";
    if (lower.includes("bigbluebutton") || lower.includes("bbb")) return "BigBlueButton";
    if (lower.includes("meet.google.com")) return "Google Meet";
    return "";
};

const inferProvider = (salle) => {
    const explicitProvider = [
        salle?.fournisseurVisio,
        salle?.provider,
        salle?.visioProvider,
        salle?.meetingProvider,
    ].find((value) => normalize(value));

    if (explicitProvider) return explicitProvider;

    const tools = normalize(salle?.tools);
    const toolsUpper = tools.toUpperCase();
    if (toolsUpper.includes("JITSI")) return "Jitsi";
    if (toolsUpper.includes("TEAMS")) return "Microsoft Teams";
    if (toolsUpper.includes("ZOOM")) return "Zoom";
    if (toolsUpper.includes("BIGBLUEBUTTON") || toolsUpper.includes("BBB")) return "BigBlueButton";
    if (toolsUpper.includes("MEET")) return "Google Meet";

    return inferProviderFromUrl(
        salle?.lienPermanent
        || salle?.lienReunion
        || salle?.meetingLink
        || salle?.visioLink
        || extractFirstUrl(salle?.tools, salle?.description)
    );
};

const resolveMeetingLink = (salle) => {
    const existingLink = [
        salle?.lienPermanent,
        salle?.lienReunion,
        salle?.meetingLink,
        salle?.visioLink,
        salle?.url,
        extractFirstUrl(salle?.tools, salle?.description),
    ].find((value) => /^https?:\/\//i.test(String(value || "").trim()));

    if (existingLink) return existingLink;

    const roomSlug = slugify(salle?.nom) || `SALLE-${salle?.id || "VIRTUELLE"}`;
    return `https://meet.jit.si/CICADAHR-SALLE-${salle?.id || "0"}-${roomSlug}`;
};

const mapSalleForDisplay = (salle) => {
    const meetingLink = resolveMeetingLink(salle);
    const provider = inferProvider(salle) || (meetingLink.includes("meet.jit.si") ? "Jitsi" : "Visio");

    return {
        ...salle,
        displayName: salle?.nom || salle?.name || "Salle virtuelle",
        capacity: salle?.maxPlaces ?? salle?.nombrePlaces ?? 0,
        provider,
        meetingLink,
    };
};

export default function SallesVirtuellesPage() {
    const dispatch = useDispatch();
    const { salles = [], loading = false, error = null } = useSelector((state) => state.salles || {});
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(fetchSallesRequest());
    }, [dispatch]);

    const virtualRooms = useMemo(() => {
        const query = normalizeUpper(search);

        return (salles || [])
            .filter((salle) => normalizeUpper(salle?.type || salle?.typeSalle) === "VIRTUELLE")
            .map(mapSalleForDisplay)
            .filter((salle) => {
                if (!query) return true;
                return [
                    salle.displayName,
                    salle.provider,
                    salle.code,
                ].some((value) => normalizeUpper(value).includes(query));
            });
    }, [salles, search]);

    return (
        <main className="vr-page">
            <header className="vr-hero">
                <div className="vr-kicker">Formation apprenant</div>
                <h1>Salles virtuelles</h1>
                <p>Retrouvez les salles virtuelles disponibles et rejoignez votre espace de formation en un clic.</p>
            </header>

            <section className="vr-toolbar">
                <div className="vr-search">
                    <Search size={16} />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher une salle virtuelle"
                    />
                </div>
                <div className="vr-toolbar__meta">
                    {loading ? "Chargement..." : `${virtualRooms.length} salle(s) virtuelle(s)`}
                </div>
            </section>

            {error ? <section className="vr-state vr-state--error">{error}</section> : null}

            {!loading && virtualRooms.length === 0 ? (
                <section className="vr-state">
                    <MonitorPlay size={18} />
                    Aucune salle virtuelle disponible pour le moment.
                </section>
            ) : (
                <section className="vr-grid">
                    {virtualRooms.map((salle) => (
                        <article className="vr-card" key={salle.id}>
                            <div className="vr-card__media">
                                <div className="vr-card__badge">
                                    <MonitorPlay size={14} />
                                    Salle virtuelle
                                </div>
                            </div>

                            <div className="vr-card__body">
                                <h2 className="vr-card__title" title={salle.displayName}>{salle.displayName}</h2>

                                <div className="vr-card__info">
                                    <span className="vr-card__pill">
                                        <Users size={14} />
                                        {salle.capacity} place{salle.capacity > 1 ? "s" : ""}
                                    </span>
                                </div>

                                <div className="vr-card__footer">
                                    <a
                                        className="vr-card__action"
                                        href={salle.meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Rejoindre
                                        <ExternalLink size={16} />
                                    </a>
                                </div>
                            </div>
                        </article>
                    ))}
                </section>
            )}
        </main>
    );
}
